import { useFlags } from "launchdarkly-react-client-sdk";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Envs, type FCWithChildren, UserLanguage } from "../../@types";
import {
  getEnvironment,
  getSubdomainForEnvironment,
} from "../../helpers/urlHelpers";
import { getLoginUrl, hasLocalSession } from "../../helpers/loginHelpers";
import { shouldDisplayGlobalHeader } from "../../helpers/routeHelpers";
import { useIsCsrSession } from "../../helpers/csrHelpers";
import { hideGlobalHeaderPaths } from "../routes";
import { useLocation } from "react-router-dom";
import { getBeneFirstAndLastName } from "../../helpers/beneficiaryInfoHelpers";
import { useTypedTranslate } from "../../helpers/intlHooks";
import {
  config,
  MCT_APP_MAIN_ID,
  MCT_GLOBAL_SKIP_NAV_ID,
  MCT_ROOT_ID,
} from "../../config";
import { setSubdomain } from "../../helpers/urlHelpers";
import { updateGlobalSkipNav } from "../../helpers/skipNavHelpers";
import { useAppState } from "../../helpers/context-hooks/useAppState";
import {
  addCssFileToHead,
  addJsFileToHead,
  getGlobalHeaderAssetEntries,
  replaceUserName,
} from "./helpers";
import { GLOBAL_HEADER_ID } from "./constants";

export const consistentHeaderTestId = "ch-root";

// Hoisted out of the component so the React Compiler doesn't have to lower a
// dynamic `import()` expression inside an effect (which it currently bails on
// with "Handle Import expressions"). The DEBUG-only `initializeLocalLogouts`
// module is still loaded lazily because this helper is only called when
// needed.
async function loadInitializeLocalLogouts(): Promise<
  MutationObserver | void | undefined
> {
  const { initializeLocalLogouts } = await import("./initializeLocalLogouts");
  return initializeLocalLogouts();
}

export const MaxGlobalHeader: FCWithChildren = ({ children }) => {
  const location = useLocation();
  // Determine whether the global (consistent) header should be displayed,
  // based on the current route - Also see `/components/Header.tsx`
  const displayGlobalHeader = shouldDisplayGlobalHeader(
    location,
    hideGlobalHeaderPaths
  );
  const { feEnableChat, feEnableAuthentication } = useFlags();
  const { beneficiary, language } = useAppState();
  const isCsr = useIsCsrSession();
  const t = useTypedTranslate();

  // * State
  const [appRootEl, setAppRootEl] = useState<HTMLDivElement | null>(null);
  const [appMainEl, setAppMainEl] = useState<HTMLDivElement | null>(null);

  // * Derived
  const hasBene = !!beneficiary;
  const beneName = hasBene
    ? getBeneFirstAndLastName(beneficiary, t)
    : undefined;

  // Takes the provided array, parses it and loads all of the JS and CSS files based on their file extension
  const importFilesFromArray = useCallback(
    (files: string[], rootUrl: string): void => {
      const cssFiles = files.filter(file => file.endsWith(".css"));
      const jsFiles = files.filter(file => file.endsWith(".js"));

      // The order matters here: CSS files should be loaded first
      for (const file of cssFiles) addCssFileToHead(rootUrl + file);
      for (const file of jsFiles) addJsFileToHead(rootUrl + file);
    },
    []
  );

  // Gets and parses the entrypoints node of the asset manifest file
  const loadFilesFromManifest = useCallback(
    (rootUrl: string, manifest: string): void => {
      const manifestUrl = rootUrl + manifest;

      getGlobalHeaderAssetEntries(manifestUrl).then(entrypoints =>
        importFilesFromArray(entrypoints, rootUrl)
      );
    },
    [importFilesFromArray]
  );

  const setHeaderProperties = useCallback(
    (globalHeader: HTMLDivElement): void => {
      const oppositeLanguage = language === UserLanguage.English ? "es" : "en";
      let langToggleLink = window.location.href.replaceAll(
        /lang=(en|es)/gim,
        `lang=${oppositeLanguage}`
      );
      const env = getEnvironment();
      if (env !== Envs.local) {
        langToggleLink = setSubdomain({
          subdomain: getSubdomainForEnvironment(),
          url: langToggleLink,
        });
      }
      const lang = language;
      const loginUrl = getLoginUrl(language);
      const logoutUrl = `${window.location.origin}${window.location.pathname}#/logout`;
      const hasSession = hasLocalSession();
      const loggedIn = hasSession && hasBene;
      const headerType = isCsr && loggedIn ? "CSR" : "MAX";
      const currentViewPage = `MCT-${location.pathname.replace("/", "")}`;

      globalHeader.setAttribute("lang", lang);
      globalHeader.setAttribute("lang-toggle-link", langToggleLink);
      globalHeader.setAttribute("login-url", loginUrl);
      globalHeader.setAttribute("logout-url", logoutUrl);
      globalHeader.setAttribute(
        "enable-live-chat",
        Boolean(feEnableChat).toString()
      );
      globalHeader.setAttribute("live-chat-viewpage", currentViewPage);
      globalHeader.setAttribute("header-type", headerType);
      globalHeader.setAttribute(
        "disable-login",
        Boolean(!feEnableAuthentication).toString()
      );
      globalHeader.dataset.testid = consistentHeaderTestId;

      if (beneName) {
        globalHeader.setAttribute("beneficiary", beneName);
      }
    },
    [
      beneName,
      feEnableChat,
      feEnableAuthentication,
      hasBene,
      isCsr,
      language,
      location.pathname,
    ]
  );

  const createGlobalHeader = useCallback((): void => {
    const globalHeader = document.createElement("div");
    globalHeader.id = GLOBAL_HEADER_ID;

    setHeaderProperties(globalHeader);

    const appRoot = document.querySelector(`#${MCT_ROOT_ID}`);

    if (appRoot) {
      setAppRootEl(appRoot as HTMLDivElement);
    }

    // Add the header as the first child of the app div
    appRoot?.prepend(globalHeader);

    const env = getEnvironment();

    const manifestUrls = {
      [Envs.local]: "https://frontend.test.medicare.gov/",
      [Envs.test]: "https://frontend.test.medicare.gov/",
      [Envs.dev]: "https://frontend.dev.medicare.gov/",
      [Envs.imp]: "https://frontend.imp.medicare.gov/",
      [Envs.prod]: "https://frontend.medicare.gov/",
    };

    const manifestUrl = manifestUrls[env];

    loadFilesFromManifest(manifestUrl, "asset-manifest.json");
  }, [loadFilesFromManifest, setHeaderProperties]);

  // Trigger to create the global header or update its attributes when values change
  // For local dev, also observes the header and creates a button for local logout
  useEffect(() => {
    let chMenuObserver: MutationObserver | void | undefined;
    (async () => {
      if (!displayGlobalHeader) {
        return;
      }
      const globalHeader = document.querySelector(`#${GLOBAL_HEADER_ID}`);
      if (globalHeader) {
        // Header is on and header already exists so update the properties
        setHeaderProperties(globalHeader as HTMLDivElement);
      } else {
        // Header is on, but no header exists in the DOM, create it
        createGlobalHeader();
      }
      if (beneficiary && config.DEBUG && !window.playwright) {
        chMenuObserver = await loadInitializeLocalLogouts();
      }
    })();
    return () => {
      chMenuObserver?.disconnect();
    };
  }, [
    createGlobalHeader,
    displayGlobalHeader,
    feEnableAuthentication,
    feEnableChat,
    location,
    setHeaderProperties,
    beneficiary,
    language,
  ]);

  /** ----- Global skip nav setup & effects ----- */
  /**
   * Set a reference to the global skip nav (in the DOM) in component state
   * This reference is used by the hook that updates the nav when the route changes
   * The skip nav is in the HTML template, so should 100% be defined when this code runs
   * Query it once here, where it can be referenced in an effect
   */
  const globalSkipNavEl = useMemo(
    () =>
      document.querySelector(
        `#${MCT_GLOBAL_SKIP_NAV_ID}`
      ) as HTMLAnchorElement | null,
    []
  );

  /**
   * Query for the MCT `main` element, if it exists at the time this code runs
   * The `main` will be used as the primary target for the global skip nav
   */
  const initialAppMainEl = useMemo(
    () =>
      document.querySelector(`#${MCT_APP_MAIN_ID}`) as HTMLDivElement | null,
    []
  );

  /**
   * Update the global skip nav on every route change
   * This has nothing directly to do with the global header, but is inserted in
   * the DOM directly above it. Without creating a new component, this seems the best
   * place to handle
   * @see `public/index.html` (where the original nav is added to HTML)
   * @see `/src/helpers/skipNavHelpers.ts` (includes a hook for custom skip nav updates)
   */
  useEffect(() => {
    if (!globalSkipNavEl || !appMainEl) {
      return;
    }
    updateGlobalSkipNav({
      skipNavEl: globalSkipNavEl,
      location,
      textContent: t("skip_to_content"),
    });
  }, [appMainEl, globalSkipNavEl, location, t]);

  /**
   * Watch the MCT "root" element (defined in our HTML template) for the MCT `main`
   * element to be added to the DOM, if it doesn't exist before this effect runs.
   * The reference to this element is saved in component
   */
  useEffect(() => {
    if (!appRootEl) {
      return;
    }
    if (initialAppMainEl) {
      setAppMainEl(initialAppMainEl);
      return;
    }
    const observerCb: MutationCallback = mutations => {
      for (const mutationRecord of mutations) {
        if (mutationRecord.type === "childList") {
          for (const node of mutationRecord.addedNodes) {
            if (node.nodeType === Node.ELEMENT_NODE) {
              if ((node as HTMLElement).id === `${MCT_APP_MAIN_ID}`) {
                setAppMainEl(node as HTMLDivElement);
                break;
              } else {
                const queriedAppMainEl = (node as HTMLElement).querySelector(
                  `#${MCT_APP_MAIN_ID}`
                );
                if (queriedAppMainEl) {
                  setAppMainEl(queriedAppMainEl as HTMLDivElement);
                  break;
                }
              }
            }
          }
        }
      }
    };
    const mo = new MutationObserver(observerCb);
    mo.observe(appRootEl, {
      childList: true,
      subtree: true,
    });
    return () => {
      mo.disconnect();
    };
  }, [appRootEl, initialAppMainEl]);

  /**
   * Effect to update the bene's name in the header
   * @TODO - If MAX updates code to observe the `beneficiary` attribute when
   * `header-type="MAX"`, this should all be able to go away
   */
  useEffect(() => {
    const globalHeader = document.querySelector(`#${GLOBAL_HEADER_ID}`);
    // globalHeader should be defined when this effect needs to run
    if (!globalHeader) {
      return;
    }
    const moCb: MutationCallback = mutations => {
      for (const mutationRecord of mutations) {
        if (
          mutationRecord.type === "attributes" &&
          mutationRecord.attributeName === "beneficiary" &&
          mutationRecord.oldValue
        ) {
          const newValue = (mutationRecord.target as Element).getAttribute(
            mutationRecord.attributeName
          );
          if (!(newValue && mutationRecord.oldValue)) {
            return;
          }
          replaceUserName(mutationRecord.oldValue, newValue);
        }
      }
    };
    const mo = new MutationObserver(moCb);
    mo.observe(globalHeader, {
      attributes: true,
      attributeFilter: ["beneficiary"],
      attributeOldValue: true,
    });
    return () => {
      mo.disconnect();
    };
  }, []);

  return <>{children}</>;
};
